/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { SelectModule } from "primeng/select";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { ScheduledOrderStatus } from "../../interfaces/scheduled-orders-filter.interface";

export interface StatusSelectionResult {
  status: ScheduledOrderStatus;
  selectedCount: number;
}

/**
 * Status Selection Dialog Component
 * Replicates the old QMC status selection functionality for bulk updates
 */
@Component({
  selector: "app-status-selection-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
  ],
  template: `
    <div class="status-selection-dialog">
      <p class="text-600 mb-3">
        Select the status to update the {{ data.selectedCount }} scheduled
        order{{ data.selectedCount !== 1 ? "s" : "" }} selected.
      </p>

      <form [formGroup]="statusForm" class="mb-3">
        <div class="field">
          <label for="status" class="block text-900 font-medium mb-2">
            Status <span class="text-red-500">*</span>
          </label>
          <p-select
            id="status"
            formControlName="status"
            [options]="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select a status"
            styleClass="w-full"
            appendTo="body"
          />
          <small
            *ngIf="
              statusForm.get('status')?.invalid &&
              statusForm.get('status')?.touched
            "
            class="p-error block mt-1"
          >
            Please select a status
          </small>
        </div>
      </form>

      <div class="flex justify-end gap-2">
        <p-button
          label="Cancel"
          severity="secondary"
          [text]="true"
          (onClick)="onCancel()"
        />
        <p-button
          label="Update Status"
          icon="pi pi-check"
          [disabled]="statusForm.invalid"
          (onClick)="onConfirm()"
        />
      </div>
    </div>
  `,
  styles: [
    `
      .status-selection-dialog {
        min-width: 400px;
      }

      @media (max-width: 576px) {
        .status-selection-dialog {
          min-width: unset;
          width: 100%;
        }

        /* Ultra-compact description text */
        p.text-600 {
          font-size: 0.813rem;
          margin-bottom: 0.75rem;
        }

        /* Ultra-compact label */
        label {
          font-size: 0.813rem;
          margin-bottom: 0.375rem;
        }

        /* Ultra-compact field spacing */
        .field {
          margin-bottom: 0.75rem;
        }

        /* Ultra-compact buttons */
        .flex.justify-end.gap-2 {
          gap: 0.5rem;
        }

        :host ::ng-deep {
          /* Compact select dropdown */
          .p-select {
            font-size: 0.813rem;
          }

          /* Ultra-thin buttons */
          .p-button {
            padding: 0.5rem 0.75rem;
            font-size: 0.813rem;
            font-weight: 500;

            .p-button-icon {
              font-size: 0.75rem;
            }
          }

          .p-button-text {
            padding: 0.5rem 0.625rem;
          }
        }
      }

      .field {
        margin-bottom: 1rem;
      }

      .field:last-child {
        margin-bottom: 0;
      }
    `,
  ],
})
export class StatusSelectionDialogComponent implements OnInit {
  statusForm: FormGroup;

  readonly statusOptions = [
    { label: "Active", value: "Active" },
    { label: "Paused", value: "Paused" },
    { label: "Failed", value: "Failed" },
    { label: "Deleted", value: "Deleted" },
  ];

  constructor(
    private fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {
    this.statusForm = this.fb.group({
      status: ["", Validators.required],
    });
  }

  get data(): { selectedCount: number } {
    return this.config.data || { selectedCount: 0 };
  }

  ngOnInit(): void {
    // Focus on the select after view init
    setTimeout(() => {
      const select = document.querySelector("p-select input") as HTMLElement;
      select?.focus();
    }, 100);
  }

  onConfirm(): void {
    if (this.statusForm.valid) {
      const result: StatusSelectionResult = {
        status: this.statusForm.value.status,
        selectedCount: this.data.selectedCount,
      };
      this.ref.close(result);
    }
  }

  onCancel(): void {
    this.ref.close(null);
  }
}
